import { z } from "zod";
import { VENDOR_KEY_REGEX } from "../lib/constants";
import { BatchPrintPreferenceSchema, ShippingDocumentsContentDisposition } from "./enums/batch-print.enum";
import { CountryCodeSchema } from "./enums/countryCode.enum";
import { VendorShipToCountriesSchema } from "./enums/crossBorder.enum";
import { tourCompletionSchema } from "./tour-completion.schema";

/** Feature name enum schema for RBAC */
export const FeatureNameSchema = z.enum(["BLP", "SS", "BC OG"]);

/** Menu group enum schema for RBAC module visibility */
export const AccessibleModuleSchema = z.enum([
  "home",
  "order",
  "orders",
  "batch",
  "batches",
  "product",
  "products",
  "labels",
  "finance",
  "finance.invoices",
  "finance.settings",
  "dashboard",
  "notifications",
  "payments",
  "settings",
]);

/** Page action enum schema */
export const PageActionSchema = z.enum(["view", "create", "edit", "delete", "export", "print", "sync"]);

/** Individual page permission object schema (new format) */
export const PagePermissionSchema = z.object({
  page: z.string(),
  actions: z.array(PageActionSchema),
});

/** Accessible pages schema for RBAC permissions */
export const AccessiblePagesSchema = z.object({
  pages: z.union([
    z.array(z.string()), // Legacy string format (e.g., "orders.view")
    z.array(PagePermissionSchema), // New object format (e.g., { page: "orders", actions: ["view"] })
  ]),
  menuGroups: z.array(AccessibleModuleSchema),
  actions: z.array(z.string()).optional(),
});

/** Vendor RBAC permissions schema */
export const VendorRBACPermissionsSchema = z.object({
  featureName: FeatureNameSchema,
  permissions: AccessiblePagesSchema,
});

export const VendorPropertiesSchema = z.object({
  labelPrintEnabledCustomerAccountNumbers: z.array(z.string()).optional(),
  batchPrintPreference: BatchPrintPreferenceSchema.optional(),
  sendOrderToVendorSystemBeforeFulfillment: z.boolean().optional(),
  isBccEnabled: z.boolean().optional(),
  blpPilotTestWebOrderNumbers: z.array(z.string()).optional(),
  customPackingSlipEnabled: z.boolean().optional(), // If enabled, the system will use custom packing slips for all FTBP (v2) orders
  // Promotion campaigns the vendor is opted into, beyond the FTBP first-free-order promotion
  // (which is gated by customPackingSlipEnabled).
  // e.g. ["ftbp_v2_first_paid_order"] opts the vendor into the FTBP first-paid-order campaign. Extend the enum for future campaigns.
  enabledPromotionCampaigns: z.array(z.enum(["ftbp_v2_first_paid_order"])).optional(),
  shippingDocumentsContentDisposition: ShippingDocumentsContentDisposition.optional(),
  labelExportUrlStyle: z.enum(["pre-signed", "pre-signed-with-auto-redirect", "ship-hero"]).optional(),
  includePreSignedUrlInExport: z.boolean().optional(),
  defaultPrintSize: z.enum(["A6", "A4", "Letter", "4x6", "3x4"]).optional(), // Default paper size for PDF printing
  defaultGroupBySettings: z.array(z.enum(["product", "grind", "quantity"])).optional(), // Default group by settings for shipping documents dialog
  // Visibility toggles for batch-summary action buttons. Undefined → treat as true.
  showShippingLabelsButton: z.boolean().optional(),
  showPackingSlipsButton: z.boolean().optional(),
  showManifestButton: z.boolean().optional(),
  // When enabled, the shipping-documents dialog stamps the PO# in a bar at the bottom
  // of each shipping label. Opt-in: undefined/false → treat as off.
  displayPoNumberOnShippingLabels: z.boolean().optional(),
  // RBAC properties (optional - can be attached to vendor)
  featureName: FeatureNameSchema.optional(),
  permissions: AccessiblePagesSchema.optional(),
  shipTo: VendorShipToCountriesSchema.array().optional(),
  // WooCommerce-only: chooses how labelPrint fulfillment is communicated back to WooCommerce.
  // Defaulted on first vendor create; treated as create-only (not in updatableVendorPropertyKeys).
  woocommerceFulfillmentMethod: z.enum(["shipmentTrackingPlugin", "fulfillmentApi"]).optional(),
  roasterLogoUrl: z.string().optional(),
  roasterLogoDamAssetId: z.string().optional(),
  // BCC guided-tour progress, one entry per user (see tour-completion.schema.ts). No entry means never done.
  tourCompletions: z.array(tourCompletionSchema).optional(),
  sellingPlatforms: z.array(z.string()).optional(), // Optional Salesforce configuration identifying the vendor's selling platforms (e.g. PBB)
});

export const vendorSchema = z.object({
  id: z.string().trim().min(1, "id is required"),
  type: z.literal("vendor"),
  region: z.string().trim().min(1, "region is required"),
  vendorKey: z
    .string()
    .trim()
    .min(1, "vendorKey is required")
    .regex(new RegExp(VENDOR_KEY_REGEX, "i"), { message: "vendorKey is invalid" }),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  vendorName: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  userId: z.string().optional(),
  contactId: z.string().optional(),
  properties: VendorPropertiesSchema.optional(),
  countryCode: CountryCodeSchema,
  /**
   * When the vendor record was created, ISO-8601. Carried from
   * `VendorDetails.createdAt` so the frontend can band roaster tenure without
   * a second request; optional because older vendor records predate the field.
   */
  createdAt: z.string().optional(),
});
export type FeatureNameValue = z.infer<typeof FeatureNameSchema>;
export type AccessibleModuleValue = z.infer<typeof AccessibleModuleSchema>;
export type PageActionValue = z.infer<typeof PageActionSchema>;
export type PagePermissionValue = z.infer<typeof PagePermissionSchema>;
export type AccessiblePagesShape = z.infer<typeof AccessiblePagesSchema>;
export type VendorRBACPermissionsShape = z.infer<typeof VendorRBACPermissionsSchema>;
export type VendorProperties = z.infer<typeof VendorPropertiesSchema>;
export type Vendor = z.infer<typeof vendorSchema>;

export type PromotionCampaign = "ftbp_v2_first_paid_order";

/** True when the vendor is opted into the given promotion campaign via the enabledPromotionCampaigns array. */
export const isPromotionCampaignEnabled = (properties: VendorProperties, campaign: PromotionCampaign): boolean =>
  properties.enabledPromotionCampaigns?.includes(campaign) ?? false;
